import { Component, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, delay, firstValueFrom, interval, map, Observable, of, shareReplay, startWith, Subject, Subscription, switchMap, take, takeUntil, takeWhile } from 'rxjs';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { NotificationService } from 'src/app/data/notification.service';
import { SignalRService } from 'src/app/data/signalr.service';
import { NotificationDto, PagedNotificationResult } from 'src/app/models/dtos/notification-dto';
import { NotificationModalComponent } from '../../notification-modal/notification-modal.component';
import { NotificationUrgencyEnum } from 'src/app/models/enums/notification-urgency-enum';
import { SubSink } from 'SubSink';

@Component({
  selector: 'app-navbar-notifications',
  templateUrl: './navbar-notifications.component.html',
  styleUrls: ['./navbar-notifications.component.scss'],
  standalone: false
})
export class NavbarNotificationsComponent implements OnInit {
  private authService = inject(AuthService);
  private signalRService = inject(SignalRService);
  private dialog = inject(MatDialog);
  private notificationService = inject(NotificationService);

  private readonly pageSize = 10;
  private readonly hubName: string = 'NotificationsHub';
  private readonly reconnectCancelation$ = new Subject<void>();
  private readonly retryConnectionDelay: number = 5000;
  private readonly retryConnectionCount: number = 120;
  private readonly ringBellDuration: number = 4000;
  private readonly subs = new SubSink();
  private workerId: number | undefined;
  private notificationsRecieverSubscription: Subscription | undefined;

  private notificationsSubject = new BehaviorSubject<NotificationDto[]>([]);
  private allCountSubject = new BehaviorSubject<number>(0);
  private unreadDotificationsCountSubject = new BehaviorSubject<number>(0);
  private ringSubject = new Subject<void>();
  public notifications$ = this.notificationsSubject.asObservable();
  public allCount$ = this.allCountSubject.asObservable();
  public unreadDotificationsCount$ = this.unreadDotificationsCountSubject.asObservable();
  public hasNewNotifications$ = this.unreadDotificationsCount$.pipe(map(count => count > 0));
  public ring$ = this.ringSubject.asObservable().pipe(switchMap(() => of(false).pipe(delay(this.ringBellDuration), startWith(true))));
  public loadedCount$ = this.notifications$.pipe(map(notifications => notifications.length));
  public showLoadMore$ = combineLatest([this.loadedCount$, this.allCount$]).pipe(map(([loadedCount, allCount]) => loadedCount < allCount));

  public get notifications(): NotificationDto[] {
    return this.notificationsSubject.value;
  }


  constructor() {
  }

  async ngOnInit(): Promise<void> {
    this.fetchNextNotificationsPage();

    this.workerId = await firstValueFrom(this.authService.getWorkerIdByAuthServerUserId());

    if (this.workerId) {
      this.startSignalRConnection();
    }
  }

  ngOnDestroy(): void {
    if (this.notificationsRecieverSubscription) {
      this.notificationsRecieverSubscription.unsubscribe();
    }

    this.reconnectCancelation$.next();
    this.reconnectCancelation$.unsubscribe();

    this.subs.unsubscribe();
  }

  private startSignalRConnection(): Observable<void> {
    if (this.notificationsRecieverSubscription) {
      this.notificationsRecieverSubscription.unsubscribe();
      this.notificationsRecieverSubscription = undefined;
    }

    const connected$ = this.signalRService.startConnection(this.hubName, this.workerId.toString(), { onclose: async () => await this.reconnect() })
      .pipe(shareReplay());

    this.notificationsRecieverSubscription = connected$
      .pipe(switchMap(() => this.signalRService.receiveNotifications()))
      .subscribe((page: PagedNotificationResult) => this.store(page));

    return connected$;
  }

  private async fetchNextNotificationsPage(): Promise<void> {
    const page = await firstValueFrom(this.notificationService.getNotificationsPaged(this.notifications.length, this.pageSize));

    this.store(page);
  }

  private store(page: PagedNotificationResult): void {
    this.ringBell(page.Results);
    this.notificationsSubject.next(this.merge<NotificationDto>(this.notifications, page.Results, (left, right) => left.Id === right.Id));
    this.allCountSubject.next(page.Count);
    this.unreadDotificationsCountSubject.next(page.UnreadCount);
    this.openModalNotifications();
  }

  private ringBell(notifications: NotificationDto[]) {
    const newestCurrent = this.notifications.find(() => true);

    if (notifications.some(n => !n.IsRead) && (!newestCurrent || notifications.some(n => n.Date > newestCurrent.Date))) {
      this.ringSubject.next();
    }
  }

  private merge<T>(left: T[], right: T[], compareFunc: (left: T, right: T) => boolean): T[] {
    const tmpRight = [...right];

    let result = left.reduce((acc, item) => {
      const index = tmpRight.findIndex(c => compareFunc(c, item));

      if (index > -1) {
        tmpRight.splice(index, 1);
      }

      acc.unshift(item);
      return acc;
    }, []);

    result = result.concat(tmpRight);

    return result.sort((a, b) => {
      return <any>new Date(b.Date) - <any>new Date(a.Date) || b.Id - a.Id;
    });
  }

  public async loadMore(event: MouseEvent): Promise<void> {
    event.stopPropagation();
    await this.fetchNextNotificationsPage();
  }

  public async openModal(notification: NotificationDto): Promise<void> {
    const dialog = this.dialog.open(NotificationModalComponent, {
      panelClass: 'details-dialog',
      data: {
        notification: notification,
      },
    });

    await firstValueFrom(dialog.afterClosed());
    await this.setAsRead(notification);
  }

  async setAsRead(notification: NotificationDto): Promise<void> {
    if (notification.IsRead) {
      return;
    }

    const notifications = [...this.notifications];

    const index = notifications.indexOf(notification);

    if (index > -1) {
      notifications[index] = { ...notification, IsRead: true };

      this.notificationsSubject.next(notifications);
      this.unreadDotificationsCountSubject.next(this.unreadDotificationsCountSubject.value - 1);
    }

    await firstValueFrom(this.notificationService.setAsRead(notification.Id));
  }

  private async reconnect(): Promise<void> {
    await firstValueFrom(
      interval(this.retryConnectionDelay)
        .pipe(startWith(0))
        .pipe(map(() => this.startSignalRConnection()))
        .pipe(takeWhile(() => this.signalRService.isConnected(this.hubName)))
        .pipe(takeUntil(this.reconnectCancelation$))
        .pipe(take(this.retryConnectionCount)));
  }

  private async openModalNotifications(): Promise<void> {
    for (const notification of this.notifications) {
      if (notification.NotificationUrgencyId === NotificationUrgencyEnum.Modal && !notification.IsRead) {
        await this.openModal(notification);
      }
    }
  }
}
